// src/components/Sidebar.jsx
import {
  Home,
  Calculator,
  BookOpen,
  CalendarDays,
  BarChart3,
  Settings as SettingsIcon,
  LogOut,
  LifeBuoy,
  Brain,
  GraduationCap 
} from "lucide-react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/GoogleAuthContext";
import { FaBrain, FaRegHeart } from "react-icons/fa";

const Sidebar = ({ accounts, selectedAccountId, setSelectedAccountId, subAllowed, hasTrapoundU }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const selectedAccount = (accounts ?? []).find((a) => a.id === selectedAccountId);

  const handleCalculateClick = () => {
    const acctType = (selectedAccount?.type || "").toLowerCase();
    const settings = selectedAccount?.settings || {};
    const asBool = (v) => v === true || (typeof v === "string" && v.toLowerCase() === "true");
    const tag = String(selectedAccount?.tag || settings.tag || "").toLowerCase();
    
    const isPropEval =
      asBool(settings.isPropFirm) ||
      asBool(settings.isProp) ||
      asBool(selectedAccount?.isPropFirm) ||
      asBool(selectedAccount?.isProp);
      
    const isPropFunded =
      asBool(settings.isPropFunded) ||
      asBool(selectedAccount?.isPropFunded) ||
      tag === "prop funded";

    if (acctType === "forex") {
      if (isPropFunded) return navigate("/forex-calculate-prop-funded");
      if (isPropEval) return navigate("/forex-calculate-prop");
      return navigate("/forex-calculate");
    } else {
      navigate("/calculate");
    }
  };

  const navClasses = (isActive) =>
    `flex flex-col items-center justify-center gap-1 h-14 min-w-[4.5rem] px-1 md:py-3 md:h-auto md:w-full md:px-0 md:rounded-xl transition ${
      isActive 
        ? "text-blue-400 md:bg-blue-800 md:text-white" 
        : "text-gray-400 md:text-white hover:text-white md:hover:bg-gray-800" 
    }`;

  const calculateIsActive =
    pathname === "/calculate" ||
    pathname === "/forex-calculate" ||
    pathname === "/forex-calculate-prop" ||
    pathname === "/forex-calculate-prop-funded";

  return (
    <aside
      className="fixed z-50 bg-gray-900 text-white flex shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.2)] md:shadow-none
        bottom-0 left-0 w-full h-16 flex-row items-center overflow-x-auto overflow-y-hidden
        md:top-[72px] md:bottom-auto md:w-20 md:h-[calc(100vh-72px)] md:flex-col md:overflow-x-hidden md:overflow-y-auto"
    >
      <div className="flex flex-row md:flex-col gap-1 md:gap-2 w-max md:w-full flex-1 md:mt-2 px-2 md:px-0 items-center">
        <NavLink to="/dashboard" className={({ isActive }) => navClasses(isActive)}>
          <Home className="w-5 h-5 md:w-6 md:h-5.5" />
          <span className="text-[10px] md:text-xs">Dashboard</span>
        </NavLink>

        <button onClick={handleCalculateClick} className={navClasses(calculateIsActive)}>
          <Calculator className="w-5 h-5 md:w-6 md:h-5.5" />
          <span className="text-[10px] md:text-xs">Entry</span>
        </button>

        <NavLink to="/journal" className={({ isActive }) => navClasses(isActive)}>
          <BookOpen className="w-5 h-5 md:w-6 md:h-5.5" />
          <span className="text-[10px] md:text-xs">Trades</span>
        </NavLink>

        <NavLink to="/events" className={({ isActive }) => navClasses(isActive)}>
          <CalendarDays className="w-5 h-5 md:w-6 md:h-5.5" />
          <span className="text-[10px] md:text-xs">News</span>
        </NavLink>

        <NavLink to="/stats" className={({ isActive }) => navClasses(isActive)}>
          <BarChart3 className="w-5 h-5 md:w-6 md:h-5.5" />
          <span className="text-[10px] md:text-xs">Stats</span>
        </NavLink>


        <NavLink to="/lessons" className={({ isActive }) => navClasses(isActive)}>
          <BookOpen className="w-5 h-5 md:w-6 md:h-5.5" />
          <span className="text-[10px] md:text-xs">Lesson</span>
        </NavLink>

        {/* 2. Added Mindset Link Here */}
        <NavLink to="/mindset" className={({ isActive }) => navClasses(isActive)}>
          <Brain className="w-5 h-5 md:w-6 md:h-5.5" />
          <span className="text-[10px] md:text-xs">Mindset</span>
        </NavLink>

        {/* University Link - Show if they bought it */}
                {hasTrapoundU && (
                  <NavLink to="/university" className={({ isActive }) => navClasses(isActive)}>
                    <GraduationCap className="w-5 h-5 md:w-6 md:h-5.5" />
                    <span className="text-[10px] md:text-xs">University</span>
                  </NavLink>
                )}

        <NavLink to="/settings" className={({ isActive }) => navClasses(isActive)}>
          <SettingsIcon className="w-5 h-5 md:w-6 md:h-5.5" />
          <span className="text-[10px] md:text-xs">Settings</span>
        </NavLink>

        

        <NavLink to="/help" className={({ isActive }) => navClasses(isActive)}>
          <LifeBuoy className="w-5 h-5 md:w-6 md:h-5.5" />
          <span className="text-[10px] md:text-xs">Help</span>
        </NavLink>
      </div>

      <div className="hidden md:block flex-1" />

      <div className="flex flex-row md:flex-col items-center justify-center md:mt-auto md:w-full md:pb-4 px-2 md:px-0 ml-auto md:ml-0 border-l border-gray-700 md:border-l-0">
        <button
          onClick={() => {
            logout();
            navigate("/");
          }}
          className="flex flex-col items-center justify-center gap-1 min-w-[4.5rem] h-14 md:h-auto md:py-3 md:w-full text-gray-400 hover:text-red-400 md:text-white md:hover:bg-red-700 md:rounded-xl transition"
        >
          <LogOut className="w-5 h-5 md:w-6 md:h-5" />
          <span className="text-[10px] md:text-xs">Logout</span>
        </button>
        <div className="hidden md:block text-[10px] text-gray-400 text-center mt-1 mb-1">
          Version 8.1
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;